import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import {
  activateBillingModulePrice,
  createBillingModulePrice,
  deactivateBillingModulePrice,
  getBillingModulePrices,
  stripeSyncBillingModulePrice,
  updateBillingModulePrice,
} from '../api/pricingApi';
import { colorTokens, typographyTokens } from '../app/designTokens';
import { useAuth } from '../auth/AuthContext';
import { hasPermission } from '../auth/permissions';
import { DataTable } from '../components/DataTable';
import { LoadingSkeleton } from '../components/LoadingSkeleton';
import { PageHeader } from '../components/PageHeader';
import { SectionCard } from '../components/SectionCard';
import { StatusBadge } from '../components/StatusBadge';
import { TableToolbar } from '../components/TableToolbar';
import { Button } from '../components/ui/Button';
import { TextInput } from '../components/ui/TextInput';

const { semantic } = colorTokens;

/** Dialog toggle: white thumb; cherry track when ON; neutral track when OFF. */
const modulePriceSwitchSx = {
  width: 42,
  height: 24,
  p: 0,
  '& .MuiSwitch-switchBase': {
    p: 0,
    m: '2px',
    color: semantic.text.primary,
    transitionDuration: '200ms',
    '&.Mui-checked': {
      transform: 'translateX(18px)',
      color: semantic.text.primary,
      '& + .MuiSwitch-track': {
        bgcolor: colorTokens.brand.primary,
        opacity: 1,
        border: 0,
      },
      '&.Mui-disabled': {
        color: semantic.text.primary,
        '& + .MuiSwitch-track': {
          bgcolor: colorTokens.brand.primary,
          opacity: 0.4,
        },
      },
    },
    '&.Mui-disabled': {
      color: semantic.text.secondary,
      '& + .MuiSwitch-track': {
        bgcolor: semantic.border.default,
        opacity: 0.4,
      },
    },
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 20,
    height: 20,
    bgcolor: semantic.text.primary,
    boxShadow: 'none',
  },
  '& .MuiSwitch-track': {
    borderRadius: 12,
    bgcolor: semantic.border.default,
    opacity: 1,
  },
};

const rowActionSx = { minHeight: 32, px: 1.25 };

const deactivateOutlineSx = {
  ...rowActionSx,
  color: semantic.error,
  border: `1px solid ${semantic.error}`,
  bgcolor: 'transparent',
  '&:hover': {
    bgcolor: 'rgba(211, 47, 47, 0.08)',
    borderColor: semantic.error,
    color: semantic.error,
  },
};

const emptyForm = {
  canonical_code: '',
  public_code: '',
  aliases: '',
  name: '',
  description: '',
  monthly_price: 0,
  currency: 'EUR',
  billing_unit: 'tier',
  is_active: true,
  is_public: true,
  sort_order: 0,
};

function formatAmount(amount, currency, t) {
  if (amount === null || amount === undefined) {
    return t('common:empty_value');
  }
  return t('pricing:currency_amount', { amount, currency: currency || 'EUR' });
}

export function BillingModulePricesPage({ variant = 'billing' }) {
  const { t } = useTranslation(['pricing', 'common']);
  const { session } = useAuth();
  const canManage = hasPermission(session, 'system.billing.module_prices.manage');
  const isPrimary = variant === 'primary';

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [rowBusyId, setRowBusyId] = useState(null);

  const emptyValue = t('common:empty_value');
  const recordCount = items.length;

  const loadItems = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await getBillingModulePrices({ per_page: 100 });
      setItems(response.data || []);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = useCallback((item) => {
    setEditing(item);
    setForm({
      canonical_code: item.canonical_code,
      public_code: item.public_code || '',
      aliases: (item.aliases || []).join(', '),
      name: item.name,
      description: item.description || '',
      monthly_price: item.monthly_price,
      currency: item.currency || 'EUR',
      billing_unit: item.billing_unit || 'tier',
      is_active: item.is_active,
      is_public: item.is_public,
      sort_order: item.sort_order || 0,
    });
    setDialogOpen(true);
  }, []);

  const buildPayload = () => ({
    canonical_code: form.canonical_code,
    public_code: form.public_code || null,
    aliases: form.aliases
      ? form.aliases.split(',').map((value) => value.trim()).filter(Boolean)
      : [],
    name: form.name,
    description: form.description || null,
    monthly_price: Number(form.monthly_price),
    currency: form.currency || 'EUR',
    billing_unit: form.billing_unit,
    is_active: form.is_active,
    is_public: form.is_public,
    sort_order: Number(form.sort_order) || 0,
  });

  const handleSave = async () => {
    setSaving(true);
    setMessage('');

    try {
      const payload = buildPayload();
      if (editing) {
        const { canonical_code: _code, ...updatePayload } = payload;
        await updateBillingModulePrice(editing.id, updatePayload);
        setMessage(t('pricing:module_prices.updated'));
      } else {
        await createBillingModulePrice(payload);
        setMessage(t('pricing:module_prices.created'));
      }
      setDialogOpen(false);
      loadItems();
    } catch {
      setMessage(t('pricing:module_prices.save_failed'));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = useCallback(async (item) => {
    setRowBusyId(item.id);
    setMessage('');
    try {
      if (item.is_active) {
        await deactivateBillingModulePrice(item.id);
      } else {
        await activateBillingModulePrice(item.id);
      }
      await loadItems();
    } catch {
      setMessage(t('pricing:module_prices.status_update_failed'));
    } finally {
      setRowBusyId(null);
    }
  }, [loadItems, t]);

  const handleStripeSync = useCallback(async (item) => {
    setRowBusyId(item.id);
    setMessage('');
    try {
      await stripeSyncBillingModulePrice(item.id);
      setMessage(t('pricing:module_prices.stripe_sync_success'));
      await loadItems();
    } catch {
      setMessage(t('pricing:module_prices.stripe_sync_failed'));
    } finally {
      setRowBusyId(null);
    }
  }, [loadItems, t]);

  const columns = useMemo(() => [
    {
      id: 'canonical_code',
      label: t('pricing:module_prices.columns.code'),
      render: (row) => (
        <Typography
          component="span"
          sx={{
            fontFamily: 'monospace',
            fontSize: 12,
            color: semantic.text.secondary,
          }}
        >
          {row.canonical_code || emptyValue}
        </Typography>
      ),
    },
    {
      id: 'name',
      label: t('common:name'),
      render: (row) => row.name || emptyValue,
    },
    ...(isPrimary ? [
      {
        id: 'public_code',
        label: t('pricing:module_prices.columns.public_code'),
        render: (row) => row.public_code || emptyValue,
      },
      {
        id: 'aliases',
        label: t('pricing:modulePricing.aliases'),
        render: (row) => (row.aliases || []).join(', ') || emptyValue,
      },
    ] : []),
    {
      id: 'monthly_price',
      label: t('pricing:module_prices.columns.monthly_price'),
      render: (row) => formatAmount(row.monthly_price, row.currency, t),
    },
    {
      id: 'billing_unit',
      label: t('pricing:module_prices.columns.billing_unit'),
      render: (row) => row.billing_unit || emptyValue,
    },
    {
      id: 'stripe',
      label: t('pricing:module_prices.columns.stripe'),
      render: (row) => (
        <StatusBadge
          status={row.stripe_price_monthly_id ? 'synced' : 'not_synced'}
          category="generic"
          apiLabel={
            row.stripe_price_monthly_id
              ? t('pricing:module_prices.stripe_synced')
              : t('pricing:module_prices.stripe_not_synced')
          }
          size="Sm"
          tone={row.stripe_price_monthly_id ? 'Success' : 'Neutral'}
        />
      ),
    },
    {
      id: 'is_active',
      label: t('common:status'),
      render: (row) => (
        <StatusBadge
          status={row.is_active ? 'active' : 'inactive'}
          category="plan"
          size="Sm"
        />
      ),
    },
    {
      id: 'actions',
      label: t('common:actions'),
      align: 'right',
      render: (row) => (
        canManage ? (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent="flex-end" alignItems="center">
            <Button
              variant="Ghost"
              onClick={() => openEdit(row)}
              disabled={rowBusyId === row.id}
              sx={rowActionSx}
            >
              {t('common:edit')}
            </Button>
            {row.is_active ? (
              <Button
                variant="Ghost"
                loading={rowBusyId === row.id}
                disabled={rowBusyId === row.id}
                onClick={() => toggleActive(row)}
                sx={deactivateOutlineSx}
              >
                {t('common:deactivate')}
              </Button>
            ) : (
              <Button
                variant="Secondary"
                loading={rowBusyId === row.id}
                disabled={rowBusyId === row.id}
                onClick={() => toggleActive(row)}
                sx={rowActionSx}
              >
                {t('common:activate')}
              </Button>
            )}
            <Button
              variant="Ghost"
              loading={rowBusyId === row.id}
              disabled={rowBusyId === row.id}
              onClick={() => handleStripeSync(row)}
              sx={rowActionSx}
            >
              {t('pricing:module_prices.stripe_sync')}
            </Button>
          </Stack>
        ) : emptyValue
      ),
    },
  ], [canManage, emptyValue, handleStripeSync, isPrimary, openEdit, rowBusyId, t, toggleActive]);

  const pageTitle = isPrimary
    ? t('pricing:modulePricing.title')
    : t('pricing:module_prices.title');
  const pageSubtitle = isPrimary
    ? t('pricing:modulePricing.description')
    : t('pricing:module_prices.subtitle');

  const errorMessage = error?.response?.data?.message
    || error?.message
    || t('pricing:module_prices.load_failed');

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <PageHeader
        type="List"
        title={pageTitle}
        subtitle={pageSubtitle}
        actions={(
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
            {!isPrimary && (
              <Button variant="Secondary" component={RouterLink} to="/billing">
                {t('common:dashboard')}
              </Button>
            )}
            {canManage && (
              <Button variant="Accent" onClick={openCreate}>
                {t('pricing:module_prices.create')}
              </Button>
            )}
          </Stack>
        )}
      />

      {isPrimary && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('pricing:plans.modulePricingSourceOfTruth')}
        </Alert>
      )}

      {isPrimary && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('pricing:plans.legacyFixedPlansHidden')}
        </Alert>
      )}

      {isPrimary && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography
            sx={{
              mb: 1,
              fontFamily: typographyTokens.fontFamily,
              fontWeight: typographyTokens.fontWeightSemibold,
              fontSize: 13,
              color: semantic.text.primary,
            }}
          >
            {t('pricing:userTiers.title')}
          </Typography>
          <Typography sx={{ fontSize: 13, color: semantic.text.secondary }}>
            {t('pricing:userTiers.oneToTen')}
          </Typography>
          <Typography sx={{ fontSize: 13, color: semantic.text.secondary }}>
            {t('pricing:userTiers.elevenToFifty')}
          </Typography>
          <Typography sx={{ fontSize: 13, color: semantic.text.secondary }}>
            {t('pricing:userTiers.fiftyOneToHundred')}
          </Typography>
          <Typography sx={{ fontSize: 13, color: semantic.text.secondary }}>
            {t('pricing:userTiers.overHundred')}
          </Typography>
          <Typography sx={{ mt: 1, fontSize: 13, color: semantic.text.secondary }}>
            {t('pricing:register.yearlyChargedMonths')}
          </Typography>
        </Alert>
      )}

      {message && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {message}
        </Alert>
      )}

      {error && !loading && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={(
            <Button variant="Ghost" onClick={loadItems} sx={{ minHeight: 32 }}>
              {t('common:retry')}
            </Button>
          )}
        >
          {errorMessage}
        </Alert>
      )}

      <SectionCard variant="Default" noPadding sx={{ width: '100%' }}>
        <TableToolbar
          mode="Default"
          title={pageTitle}
          meta={(
            <StatusBadge
              status="list"
              category="generic"
              apiLabel={t('pricing:module_prices.toolbar_count', { count: recordCount })}
              size="Sm"
              tone="Neutral"
            />
          )}
        />

        {loading ? (
          <LoadingSkeleton
            type="Table"
            embedded
            rows={3}
            columns={columns.length}
          />
        ) : (
          <DataTable
            type="Default"
            embedded
            columns={columns}
            rows={items}
            getRowKey={(row) => row.id}
            emptyMessage={t('pricing:module_prices.no_items')}
            sx={{
              overflowX: 'auto',
              width: '100%',
              '& table': {
                width: '100%',
                minWidth: '100%',
              },
            }}
          />
        )}
      </SectionCard>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: semantic.bg.surface,
            backgroundImage: 'none',
            border: `1px solid ${semantic.border.subtle}`,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: typographyTokens.fontFamily,
            fontWeight: typographyTokens.fontWeightSemibold,
            fontSize: 16,
            color: semantic.text.primary,
          }}
        >
          {editing ? t('pricing:module_prices.edit') : t('pricing:module_prices.create')}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextInput
              label={t('pricing:module_prices.fields.canonical_code')}
              value={form.canonical_code}
              onChange={(e) => setForm({ ...form, canonical_code: e.target.value })}
              disabled={Boolean(editing)}
              required
            />
            <TextInput
              label={t('pricing:module_prices.fields.public_code')}
              value={form.public_code}
              onChange={(e) => setForm({ ...form, public_code: e.target.value })}
            />
            <TextInput
              label={t('pricing:module_prices.fields.aliases')}
              value={form.aliases}
              onChange={(e) => setForm({ ...form, aliases: e.target.value })}
            />
            <TextInput
              label={t('common:name')}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
            <TextInput
              label={t('common:description')}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              multiline
              minRows={2}
            />
            <TextInput
              label={t('pricing:module_prices.fields.monthly_price')}
              type="number"
              value={form.monthly_price}
              onChange={(e) => setForm({ ...form, monthly_price: e.target.value })}
              required
            />
            <TextInput
              label={t('pricing:module_prices.fields.currency')}
              value={form.currency}
              onChange={(e) => setForm({ ...form, currency: e.target.value })}
            />
            <TextInput
              label={t('pricing:module_prices.fields.billing_unit')}
              value={form.billing_unit}
              onChange={(e) => setForm({ ...form, billing_unit: e.target.value })}
            />
            <TextInput
              label={t('pricing:module_prices.fields.sort_order')}
              type="number"
              value={form.sort_order}
              onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
            />
            <FormControlLabel
              control={(
                <Switch
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                  sx={modulePriceSwitchSx}
                />
              )}
              label={t('pricing:module_prices.fields.is_active')}
              sx={{
                m: 0,
                alignItems: 'center',
                gap: 1,
                color: semantic.text.secondary,
                '& .MuiFormControlLabel-label': {
                  fontSize: 13,
                  lineHeight: '20px',
                },
              }}
            />
            <FormControlLabel
              control={(
                <Switch
                  checked={form.is_public}
                  onChange={(e) => setForm({ ...form, is_public: e.target.checked })}
                  sx={modulePriceSwitchSx}
                />
              )}
              label={t('pricing:module_prices.fields.is_public')}
              sx={{
                m: 0,
                alignItems: 'center',
                gap: 1,
                color: semantic.text.secondary,
                '& .MuiFormControlLabel-label': {
                  fontSize: 13,
                  lineHeight: '20px',
                },
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button variant="Ghost" onClick={() => setDialogOpen(false)}>
            {t('common:cancel')}
          </Button>
          <Button variant="Accent" onClick={handleSave} loading={saving} disabled={saving}>
            {t('common:save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
